import { useTranslation } from "react-i18next";
import { CalendarClock, Heart, PackageOpen, ShieldAlert, Syringe, TriangleAlert, Users } from "lucide-react";
import { Avatar } from "@akuna/ui";
import { supabase } from "@/integrations/supabase/client";
import { initials } from "@/lib/utils/string-utils";
import { cn } from "@/lib/utils";
import { daysOverdue, petAlertColorPresentation, splitRelationshipRows } from "./petProfileModel";
import type { PetReservationSummary } from "./usePetReservationSummary";
import { buildPetProfileReadModel, type PetProfileReadChip } from "./petProfileReadModel";

const formatDate = (value: string | null | undefined, locale = "pt-BR") => {
  if (!value) return "—";
  const date = new Date(`${value.slice(0, 10)}T12:00:00`);
  return new Intl.DateTimeFormat(locale.startsWith("en") ? "en-US" : "pt-BR").format(date);
};

const petPhotoUrl = (path: string | null | undefined) =>
  path ? supabase.storage.from("pet-photos").getPublicUrl(path).data.publicUrl : undefined;

const alertColorClasses: Record<string, string> = {
  orange: "border-orange-200 bg-orange-50 text-orange-700",
  amber: "border-amber-200 bg-amber-50 text-amber-800",
  blue: "border-blue-200 bg-blue-50 text-blue-700",
  purple: "border-purple-200 bg-purple-50 text-purple-700",
  green: "border-green-200 bg-green-50 text-green-700",
};

export function PetProfileReadSummary({ summary }: { summary: PetReservationSummary }) {
  const { t, i18n } = useTranslation("daycareHotel");
  const model = buildPetProfileReadModel(summary);
  const { likes, dislikes, neutral } = splitRelationshipRows(model.relationships);
  const hasRelationships = model.relationships.length > 0;
  const hasStoredItems = model.storedItems.length > 0;
  const hasLowerContext = hasRelationships || hasStoredItems;

  if (model.chips.length === 0 && !model.notes && !hasLowerContext) return null;

  const renderChip = (chip: PetProfileReadChip) => {
    if (chip.kind === "alert") {
      const presentation = petAlertColorPresentation(chip.color);
      const semanticColorClass = chip.color ? alertColorClasses[chip.color] : undefined;
      return (
        <li key={`alert-${chip.id}`} className={cn("inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium", presentation.customColor ? "bg-card" : semanticColorClass ?? "border-state-danger/30 bg-state-danger-soft/50 text-state-danger")} style={presentation.customColor ? { borderColor: presentation.customColor, color: presentation.customColor, backgroundColor: `${presentation.customColor}18` } : undefined}>
          <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="truncate">{t("petProfile.alertChip", { value: chip.text })}</span>
        </li>
      );
    }

    if ("names" in chip) {
      const Icon = chip.kind === "allergy" ? TriangleAlert : Heart;
      return (
        <li key={chip.kind} className={cn("inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium", chip.kind === "allergy" ? "border-akuna-amber/30 bg-akuna-amber/10 text-akuna-amber" : "border-akuna-blue/30 bg-akuna-blue/10 text-akuna-blue")}>
          <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="truncate">{t(`petProfile.${chip.kind}Chip`, { value: chip.names.join(" · ") })}</span>
        </li>
      );
    }

    const overdue = chip.kind === "application-overdue";
    const application = chip.application;
    return (
      <li key={`${chip.kind}-${application.id}`} className={cn("inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium", overdue ? "border-state-danger/30 bg-state-danger-soft/50 text-state-danger" : "border-state-success/30 bg-state-success-soft/50 text-state-success")}>
        {overdue ? <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden="true" /> : <Syringe className="h-4 w-4 shrink-0" aria-hidden="true" />}
        <span className="min-w-0 whitespace-normal break-words">
          {t(overdue ? "petProfile.applicationOverdueChip" : "petProfile.applicationUpcomingChip", {
            name: application.name,
            count: daysOverdue(application.nextDoseDate ?? ""),
            date: formatDate(application.nextDoseDate, i18n?.language ?? "pt-BR"),
          })}
        </span>
      </li>
    );
  };

  const relationshipTones: Record<string, { icon: typeof Heart; className: string; labelKey: string }> = {
    dislikes: { icon: TriangleAlert, className: "border-state-danger/30 bg-state-danger-soft/30 text-state-danger", labelKey: "petProfile.relationshipDislikesTag" },
    neutral: { icon: Users, className: "border-border bg-muted text-muted-foreground", labelKey: "petProfile.relationshipNeutralTag" },
    likes: { icon: Heart, className: "border-state-success/30 bg-state-success-soft/30 text-state-success", labelKey: "petProfile.relationshipLikesTag" },
  };

  const orderedRelationships = [...dislikes, ...neutral, ...likes];

  const renderRelationship = (relationship: (typeof model.relationships)[number]) => {
    const tone = relationshipTones[relationship.relationship] ?? relationshipTones.neutral;
    const Icon = tone.icon;
    return (
      <li key={relationship.id} aria-label={t(tone.labelKey, { name: relationship.petName })} className={cn("inline-flex min-w-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm", tone.className)}>
        <Avatar className="h-6 w-6 shrink-0" name={relationship.petName} src={petPhotoUrl(relationship.petPhotoPath)} fallback={initials(relationship.petName)} />
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">{relationship.petName}</span>
      </li>
    );
  };

  return (
    <div className="grid gap-3">
      {model.chips.length > 0 ? <ul aria-label={t("petProfile.operationalSummary")} className="flex flex-wrap gap-2 ">{model.chips.map(renderChip)}</ul> : null}

      {model.notes || hasLowerContext ? <div className="grid gap-2 lg:grid-cols-2">

        {hasStoredItems ? <ContextBlock icon={PackageOpen} label={t("petProfile.storedItems")}>
          <ul className="flex flex-wrap gap-1.5">{model.storedItems.map((item) => <li key={item.id} className="rounded-full border border-akuna-blue/30 bg-akuna-blue/10 px-2.5 py-1 text-sm text-akuna-blue">{item.itemName}{item.storageLocation ? ` · ${item.storageLocation}` : null}</li>)}</ul>
        </ContextBlock> : null}

        {hasRelationships ? <ContextBlock icon={Users} label={t("petProfile.relationships")}>
          <ul className="flex flex-wrap gap-1.5">{orderedRelationships.map(renderRelationship)}</ul>
        </ContextBlock> : null}

        {model.notes ? <ContextBlock className="lg:col-span-2" icon={CalendarClock} label={t("petProfile.notesDaycareHotel")}>
          <p className="whitespace-pre-line text-sm text-muted-foreground">{model.notes}</p>
        </ContextBlock> : null}
      </div> : null}
    </div>
  );
}

function ContextBlock({ icon: Icon, label, children, className }: { icon: typeof Heart; label: string; children: JSX.Element; className?: string }) {
  return (
    <section className={cn("flex flex-col gap-1.5 rounded-lg bg-muted p-3 sm:flex-row sm:items-start sm:gap-3", className)}>
      <div className="flex shrink-0 items-center gap-2 text-sm font-semibold text-foreground">
        <span className="truncate">{label}</span>
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </section>
  );
}
